import React, { useState } from 'react';
import { MessageError } from '@/types/messages';
import { MessageErrorHandler } from '@/utils/messageErrorHandler';

export interface MessageInputProps {
  onSendMessage: (content: string) => Promise<void>;
  conversationId: string;  // Add this to identify which conversation has errors
  error?: MessageError;    // Add this to display errors
  onClearError?: () => void; // Add this to clear errors
}

export function MessageInput({ 
  onSendMessage, 
  conversationId,
  error,
  onClearError 
}: MessageInputProps) {
  const [message, setMessage] = useState('');
  const [isSending, setIsSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || isSending) return;
    
    setIsSending(true);
    try {
      await onSendMessage(message.trim());
      setMessage('');
      if (onClearError) {
        onClearError();
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleRetry = async () => {
    if (!message.trim()) return;
    await handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  return (
    <div className="border-t">
      {error && (
        <div className="px-4 py-2 bg-red-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-red-600">{error.message}</p>
            {MessageErrorHandler.isRetryableError(error) && (
              <button 
                onClick={handleRetry}
                className="text-sm text-blue-600 hover:text-blue-800"
                disabled={isSending}
              >
                Retry
              </button>
            )}
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit} className="p-4">
        <div className="flex gap-2">
          <input
            type="text"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 rounded-lg border px-4 py-2 text-gray-900 bg-white"
            disabled={isSending}
          />
          <button
            type="submit"
            disabled={!message.trim() || isSending}
            className="bg-blue-500 text-white px-4 py-2 rounded-lg disabled:opacity-50"
          >
            Send
          </button>
        </div>
      </form>
    </div>
  );
}